import { useMutation, useQueryClient } from 'react-query';

import { KimThread } from '../../../../backend/src/matter/subset/thread/kim-thread.entity';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

type PartialKimThread = Pick<KimThread, 'id' | 'name' | 'createdDate'>;

export const useCreateKimThread = (matterId?: string, subsetId?: string) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();
  const queryKey = [QueryEntity.KimThreads, { subsetId }];

  return useMutation(
    async (message: string) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: { message },
      });

      const response = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/thread`,
        fetchConfig,
      );
      if (!response.ok) {
        throw new Error('Creating Kim thread failed');
      }
      return response.json();
    },
    {
      onMutate: async (message: string) => {
        await queryClient.cancelQueries(queryKey);
        const previous = queryClient.getQueryData<PartialKimThread[]>(queryKey);

        const optimisticThread: PartialKimThread = {
          id: `optimistic-${Date.now()}`,
          name: message.replace(/@\[([^\]]+)\]\([^)]+\)/g, '$1').slice(0, 200),
          createdDate: new Date(),
        };

        queryClient.setQueryData<PartialKimThread[]>(queryKey, (old) => [optimisticThread, ...(old ?? [])]);
        return { previous };
      },
      onSuccess: () => {
        trackCustomEvent(TrackingEventsV2.KimCreateThread, { matterId, subsetId });
        setTimeout(() => queryClient.invalidateQueries(queryKey), 3000);
      },
      onError: (_err, _message, context) => {
        if (context?.previous) {
          queryClient.setQueryData(queryKey, context.previous);
        }
      },
    },
  );
};
